import { Fragment, useState, useEffect } from "react";
import { ScrollRestoration, useSearchParams } from "react-router-dom";
import LoadingPlaceholder from "../../components/ui/loading/LoadingPlaceholder";

function ServiceAgreement() {
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get("embed") === "1";
  const [loading, setLoading] = useState(true);

  // Simulate loading for consistency and future API calls
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Fragment>
      <ScrollRestoration />
      <main className={isEmbed ? "" : "public"}>
        <div className={isEmbed ? "" : "content"}>
          {loading ? (
            <LoadingPlaceholder rows={3} type="card-large" />
          ) : (
            <div id="terms">
              <h1>Plaintr Service Agreement</h1>

              <h3>1. Introduction &amp; Parties</h3>
              <p>
                Welcome to Plaintr’s professional network. This Agreement
                defines how we work together to deliver trusted, high-quality
                tax services to our clients. It sets clear expectations so you
                know exactly how we operate and how we protect both you and our
                clients.
              </p>
              <h4>1.1 Parties</h4>
              <p>
                This Service Agreement (“Agreement”) is entered into by and
                between Plaintr, LLC. (“Plaintr”) and the undersigned certified
                tax professional (“Pro”), collectively referred to as the
                “Parties.”
              </p>

              <h3>2. Scope of Engagement</h3>
              <p>
                We’re a bridge between clients and certified tax professionals.
                This section outlines exactly what your role is when you work
                with a client through Plaintr.
              </p>
              <h4>2.1 Role</h4>
              <p>
                Pro is engaged as an independent contractor to provide tax
                preparation, tax planning, and related advisory services to
                clients matched via the Plaintr platform.
              </p>
              <h4>2.2 No Employment Relationship</h4>
              <p>
                Nothing in this Agreement shall be construed as creating an
                employment relationship between Plaintr and the Pro.
              </p>

              <h3>3. Onboarding &amp; Approval Criteria</h3>
              <p>
                Only top-tier professionals are approved to join our network.
                This keeps trust high and ensures consistent service quality.
              </p>
              <h4>3.1 Eligibility</h4>
              <p>
                Only certified CPAs, Enrolled Agents (EAs), or Tax Attorneys may
                apply.
              </p>
              <h4>3.2 Verification &amp; Approval Process</h4>
              <p>
                Plaintr reserves the right to verify credentials, request
                additional documentation, and reject applications without
                explanation.
              </p>
              <h4>3.3 Mandatory Onboarding Steps</h4>
              <ol>
                <li>
                  Account Creation – Pro creates their Plaintr account and
                  provides basic contact information.
                </li>
                <li>
                  Platform Knowledge Test – Pro must complete and pass an
                  onboarding test covering platform use, SLA requirements, and
                  confidentiality rules.
                </li>
                <li>
                  Credential Submission – Pro must submit proof of CPA, EA, or
                  Tax Attorney certification, and any additional documents
                  requested by Plaintr.
                </li>
                <li>
                  Credential Verification – Plaintr will validate all
                  credentials, licenses, and standing with relevant
                  state/federal boards.
                </li>
                <li>
                  Payout Account Setup – Pro must create a connected account to
                  enable payouts via Plaintr’s payment processor.
                </li>
                <li>
                  Training Completion – Pro must attend the initial onboarding
                  training session and receive account activation approval
                  before accepting clients.
                </li>
              </ol>
              <p>
                Plaintr reserves the right to reject applications at any step
                without obligation to provide a reason.
              </p>

              <h3>4. Service Level Expectations (SLA)</h3>
              <p>
                Fast, clear communication builds trust and keeps cases moving.
                Our SLA sets minimum standards for responsiveness and updates.
              </p>
              <h4>4.1 Initial Client Response</h4>
              <p>
                Respond to all new client messages within one (1) business hour
                from the time the case is accepted in the dashboard.
              </p>
              <h4>4.2 Ongoing Engagement Response</h4>
              <p>
                Respond to all client messages during an active engagement
                within twelve (12) business hours unless a different timeframe
                is agreed with the client and documented in the dashboard.
              </p>
              <h4>4.3 Proactive Updates</h4>
              <p>
                If there is no client activity, provide a status update in the
                dashboard at least once every two (2) business days until the
                engagement is completed.
              </p>
              <h4>4.4 Dashboard Use</h4>
              <p>
                All official case updates, deliverables, and important client
                communications must be documented in the dashboard.
              </p>

              <h3>5. Payment Terms &amp; Schedule</h3>
              <p>We pay for completed, approved work—on time, every time.</p>
              <h4>5.1 Payment Flow</h4>
              <p>
                Pros are paid the full rate as it is set and agreed to before
                accepting the engagement. The platform fee is charged to the
                client in addition to the pro’s rate and does not reduce the
                pro’s earnings under this Agreement.
              </p>
              <h4>5.2 Payment Schedule</h4>
              <p>
                Plaintr issues payouts once per week on Wednesdays via Stripe.
                All cases completed before the weekly payout cut-off are
                included in that week’s payment. Stripe’s standard processing
                time (2–3 business days) applies.
              </p>
              <h4>5.3 Conditions for Payment</h4>
              <p>
                Payment is contingent on meeting SLA requirements and compliance
                obligations.
              </p>
              <h4>5.4 Withholding Rights</h4>
              <p>
                Plaintr may withhold payment in cases of SLA breach, dispute, or
                incomplete work. In the event of a dispute or refund request,
                funds will be securely held and managed in accordance with
                Section 8 (Dispute Resolution) and Section 9 (Refund Policy).
                Funds subject to a dispute shall remain on hold as further
                detailed in the Dispute Resolution Policy §11.
              </p>

              <h3>6. Confidentiality &amp; Data Handling</h3>
              <p>
                Clients trust you with sensitive financial data. Protecting it
                is non-negotiable.
              </p>
              <h4>6.1 Use Limitation</h4>
              <p>Client data may only be used for the agreed engagement.</p>
              <h4>6.2 Non-Disclosure</h4>
              <p>
                Pro may not share, sell, or disclose client data to unauthorized
                parties.
              </p>
              <h4>6.3 Retention &amp; Deletion</h4>
              <p>
                Pro must delete/destroy all copies of client data after
                engagement completion and confirm deletion upon request.
              </p>
              <h4>6.4 Audit Rights</h4>
              <p>
                Plaintr may request confirmation of compliance with this
                section.
              </p>

              <h3>7. Pro Conduct &amp; Off-Platform Rules</h3>
              <p>
                You can choose how you communicate, but the platform stays the
                official home for your work and payments.
              </p>
              <h4>7.1 Client Communication</h4>
              <p>
                You may communicate with clients outside the dashboard when
                necessary to complete the engagement, but all deliverables and
                important updates must be uploaded to the dashboard.
              </p>
              <h4>7.2 Payment Flow</h4>
              <p>
                All payments must be processed through Plaintr. No direct
                payment from clients is permitted.
              </p>
              <h4>7.3 Future Work</h4>
              <p>
                Any new engagements with clients you met through Plaintr during
                this Agreement must be booked through Plaintr unless otherwise
                approved in writing.
              </p>
              <h4>7.4 Enforcement</h4>
              <p>
                Violation may result in account termination, forfeiture of
                unpaid earnings, and legal action if client data privacy is
                breached.
              </p>

              <h3>8. Dispute Resolution – Internal Resolution Panel</h3>
              <p>
                All disputes or complaints arising between Pro and Client will
                be handled under Plaintr’s Internal Resolution Panel (“IRP”).
              </p>
              <ol>
                <li>
                  All disputes arising under this Agreement shall be resolved in
                  accordance with Plaintr’s Dispute Resolution Policy (“DRP”),
                  as amended from time to time.
                </li>
                <li>
                  The DRP includes both internal resolution by Plaintr’s
                  Internal Resolution Panel (IRP) and escalation rights to
                  arbitration before the American Arbitration Association (AAA).
                  The Parties expressly agree that any AAA arbitration conducted
                  under the DRP shall be final, binding, and enforceable under
                  the Federal Arbitration Act.
                </li>
              </ol>
              <h4>8.3 Dispute Submission Process</h4>
              <ol>
                <li>
                  Disputes may be initiated by either Client or Pro via the
                  Plaintr dashboard or by email to disputes@plaintr.com.
                </li>
                <li>
                  Submissions must include Case ID, description, and supporting
                  evidence.
                </li>
                <li>
                  Plaintr will acknowledge receipt within one (1) business day
                  and assign the case to the IRP.
                </li>
              </ol>
              <h4>8.4 Evidence Requirements</h4>
              <ol>
                <li>
                  Clients must submit communication logs, required documents,
                  and proof of payment.
                </li>
                <li>
                  Pro must submit communication logs, work product, and (if
                  applicable) proof of Client non-cooperation.
                </li>
                <li>
                  Evidence must be submitted within three (3) business days of
                  initiation. Late evidence is permitted only if it constitutes
                  material new evidence.
                </li>
              </ol>
              <h4>8.5 Resolution Timeline</h4>
              <p>
                The Internal Review Panel (IRP) shall issue a decision within 10
                business days (3 business days for urgent cases). For
                consistency, all deadlines under the IRP process are measured in
                business days, not calendar days.
              </p>
              <h4>8.6 Outcomes</h4>
              <p>
                Decisions may include: (a) full or partial refund, (b)
                corrective work, (c) reassignment, or (d) full payment release
                to the Pro.
              </p>
              <h4>8.7 Appeals</h4>
              <p>
                IRP decisions are not subject to internal appeal once rendered,
                except in cases of manifest error in calculation or fraud.
                Escalation to AAA arbitration under the Dispute Resolution
                Policy remains the only external appeal mechanism.
              </p>
              <h4>8.8 Recordkeeping</h4>
              <p>
                IRP decisions are final and binding, except in cases of clear
                error, fraud, or manifest injustice. In such limited
                circumstances, a party may request correction by the IRP within
                five (5) business days of the decision. No further internal
                appeal is permitted; unresolved matters shall proceed under the
                arbitration framework of the Dispute Resolution Policy.
              </p>

              <h3>9. Refund Policy</h3>
              <h4>9.1 Eligibility</h4>
              <p>A Client may be entitled to a refund if:</p>
              <ol>
                <li>Work was not performed despite Client cooperation;</li>
                <li>
                  Services contained material errors or omissions affecting the
                  Client’s tax position, as confirmed by the IRP;
                </li>
                <li>The Pro materially breached this Agreement; or</li>
                <li>
                  A Plaintr matching error resulted in assignment of a Pro who
                  did not meet credential requirements.
                </li>
              </ol>
              <h4>9.2 Exclusions</h4>
              <p>Refunds are not available if:</p>
              <ol>
                <li>The Client fails to provide required information;</li>
                <li>
                  Work was delivered as agreed but the Client changes their
                  mind;
                </li>
                <li>
                  IRS or state outcomes are unrelated to Pro performance; or
                </li>
                <li>The request is made outside the applicable timelines.</li>
              </ol>
              <h4>9.3 Refund Requests</h4>
              <p>
                Refunds must be requested via the dashboard or
                refunds@plaintr.com within thirty (30) days of the engagement
                completion date or agreed deadline.
              </p>
              <h4>9.4 Review and Decision</h4>
              <p>
                Refund requests will be reviewed by the IRP if linked to a
                dispute, or by Plaintr Support if no dispute exists. A decision
                will be issued within ten (10) business days.
              </p>
              <h4>9.5 Issuance</h4>
              <p>
                Approved refunds will be issued within five (5) business days
                after approval.
              </p>
              <h4>9.6 Payment Handling</h4>
              <p>
                During a dispute, funds will be securely held until the IRP
                issues a decision, in coordination with Section 5.4 (Withholding
                Rights). Outcomes may include refund to Client, release of
                payment to Pro, or partial allocations.
              </p>
              <h4>9.7 Compliance</h4>
              <p>
                This Refund Policy is designed to comply with FTC rules and
                applicable state laws. Where state law grants additional
                consumer rights, Plaintr will honor those rights.
              </p>

              <h3>10. Termination &amp; Reassignment</h3>
              <h4>10.1 Reassignment by Plaintr</h4>
              <p>
                Plaintr may reassign a client for breach, SLA failure, or at
                client request if justified.
              </p>
              <h4>10.2 Pro-Initiated Reassignment</h4>
              <p>
                Pros may request reassignment with valid reason and approval.
              </p>
              <h4>10.3 Termination</h4>
              <p>
                Plaintr may terminate this Agreement immediately for breach of
                confidentiality, SLA failure, or other serious misconduct.
              </p>

              <h3>11. Indemnification</h3>
              <p>
                Pro agrees to indemnify, defend, and hold harmless Plaintr, its
                affiliates, officers, and employees from any claims, damages,
                liabilities, or expenses arising from: (a) Pro’s breach of this
                Agreement; (b) negligence or misconduct; or (c) violation of any
                applicable law or regulation.
              </p>

              <h3>12. Intellectual Property Rights Over Work Product</h3>
              <p>
                All work products delivered to clients through the Plaintr
                platform shall be deemed “work made for hire” for the purposes
                of the engagement. Upon full payment, all rights in the work
                product shall transfer to the client, except for any
                pre-existing intellectual property of the Pro.
              </p>

              <h3>13. Explicit Data Security Compliance</h3>
              <p>
                Pro must comply with all applicable data protection laws and
                regulations, including but not limited to IRS Publication 4557
                (Safeguarding Taxpayer Data). Failure to comply constitutes
                grounds for immediate termination and may result in legal
                action.
              </p>

              <h3>14. Termination Without Cause</h3>
              <h4>14.1 Notice Requirement</h4>
              <p>
                Either Party may terminate this Agreement without cause by
                providing written notice of termination at least ten (10)
                business days in advance.
              </p>
              <h4>14.2 Ongoing Client Engagements</h4>
              <ol>
                <li>
                  The Pro shall continue to diligently perform all services for
                  any Client engagements already in progress.
                </li>
                <li>
                  If a filing or other deliverable is due within the notice
                  period, the Pro must complete such deliverable unless Plaintr,
                  the Client, and the Pro mutually agree in writing to an
                  alternative arrangement.
                </li>
              </ol>
              <h4>14.3 Reassignment of Clients</h4>
              <p>
                Plaintr reserves the right to reassign affected Clients to
                another Pro during the notice period to ensure continuity of
                service.
              </p>
              <h4>14.4 Waiver or Shortening of Notice</h4>
              <ol>
                <li>no active Client engagements remain; or</li>
                <li>
                  Plaintr has arranged an alternative Pro to assume the
                  engagement without disruption.
                </li>
              </ol>
              <h4>14.5 Ethical and Regulatory Obligations</h4>
              <p>
                In no event shall a Pro terminate this Agreement in a manner
                that constitutes client abandonment under applicable IRS, state,
                or professional board rules.
              </p>

              <h3>15. Jurisdiction &amp; Venue</h3>
              <p>
                This Agreement shall be governed by and construed under the laws
                of the State of New York. Any legal proceedings shall be brought
                exclusively in the state or federal courts located in New York
                County, NY.
              </p>

              <h3>16. Miscellaneous</h3>
              <h4>16.1 Entire Agreement</h4>
              <p>
                This Agreement constitutes the entire understanding between the
                Parties.
              </p>
              <h4>16.2 Amendments</h4>
              <p>
                Plaintr may update this Agreement by posting a revised version
                on the platform.
              </p>

              <h3>17. Acceptance</h3>
              <p>
                By clicking “I Agree” or by accepting an engagement via the
                Plaintr platform, you agree to the terms of this Service
                Agreement.
              </p>
            </div>
          )}
        </div>
      </main>
    </Fragment>
  );
}

export default ServiceAgreement;
